import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import FormInput from "../../../components/UI/FormInput";
import FormSelect from "../../../components/UI/FormSelect";
import CatalogNameAutocomplete from "../../../components/UI/CatalogNameAutocomplete";
import { formatPhone } from "../../Application/application.validation";
import ZipCodeInput from "./ZipCodeInput";
import {
  PRIMARY_FACILITY_FIELD_CONFIG,
  applyPrimaryFacilityCatalogRow,
  getPrimaryFacilityOptionKey,
  getPrimaryFacilityOptionLabel,
  getPrimaryFacilityOptionSubtitle,
  searchPrimaryFacilityCatalog,
} from "../primaryFacilityCatalog";

export {
  PRIMARY_FACILITY_FIELD_KEYS,
} from "../primaryFacilityCatalog";

export const PrimaryFacilityEditFields = ({
  variant,
  control,
  register,
  setValue,
  errors,
  typeOptions,
}) => {
  const c = PRIMARY_FACILITY_FIELD_CONFIG[variant];
  const searchFn = useMemo(
    () => (query, ctx) => searchPrimaryFacilityCatalog(variant, query, ctx),
    [variant],
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <Controller
          name={c.nameField}
          control={control}
          render={({ field }) => (
            <CatalogNameAutocomplete
              label={c.nameLabel}
              placeholder={c.placeholder}
              hint={c.hint}
              value={field.value || ""}
              onChange={field.onChange}
              searchFn={searchFn}
              getOptionLabel={(row) => getPrimaryFacilityOptionLabel(variant, row)}
              getOptionKey={(row) => getPrimaryFacilityOptionKey(variant, row)}
              getOptionSubtitle={(row) =>
                getPrimaryFacilityOptionSubtitle(variant, row)
              }
              onSelect={(row) => {
                applyPrimaryFacilityCatalogRow(variant, row, setValue);
                field.onChange(getPrimaryFacilityOptionLabel(variant, row));
              }}
              error={errors[c.nameField]?.message}
            />
          )}
        />
        <FormSelect
          size="sm"
          label={c.typeLabel}
          name={c.typeField}
          register={register}
          options={typeOptions}
          placeholder="Select"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
        <FormInput
          size="sm"
          label="Address"
          name={c.addressField}
          register={register}
          className="sm:col-span-1"
        />
        <FormInput size="sm" label="City" name={c.cityField} register={register} />
        <FormInput size="sm" label="State" name={c.stateField} register={register} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
        <Controller
          name={c.zipField}
          control={control}
          render={({ field }) => (
            <ZipCodeInput
              label="ZIP"
              value={field.value}
              onChange={field.onChange}
              error={errors[c.zipField]?.message}
            />
          )}
        />
        <FormInput
          size="sm"
          label="Phone"
          name={c.phoneField}
          register={register}
          error={errors[c.phoneField]}
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          onInput={(e) => {
            e.target.value = formatPhone(e.target.value);
          }}
        />
        <FormInput
          size="sm"
          label="Fax"
          name={c.faxField}
          register={register}
          error={errors[c.faxField]}
          placeholder="(XXX)XXX-XXXX"
          maxLength={13}
          onInput={(e) => {
            e.target.value = formatPhone(e.target.value);
          }}
        />
      </div>
    </div>
  );
};

export const PrimaryFacilityViewFields = ({ variant, patient, Field, formatPhoneFn }) => {
  const c = PRIMARY_FACILITY_FIELD_CONFIG[variant];
  const get = (key) => patient?.[key];

  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
        <Field label={c.nameLabel} value={get(c.nameField)} />
        <Field label={c.typeLabel} value={get(c.typeField)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-3">
        <Field label="Address" value={get(c.addressField)} />
        <Field label="City" value={get(c.cityField)} />
        <Field label="State" value={get(c.stateField)} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-3">
        <Field label="ZIP" value={get(c.zipField)} />
        <Field
          label="Phone"
          value={formatPhoneFn(get(c.phoneField) || "") || get(c.phoneField)}
        />
        <Field
          label="Fax"
          value={formatPhoneFn(get(c.faxField) || "") || get(c.faxField)}
        />
      </div>
    </div>
  );
};
